import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import update from 'immutability-helper';
import { X, Check } from 'react-feather';
import classNames from 'classnames';
import { orderBy, isEqual, xor } from 'lodash';
import { useFetch } from 'hooks';
import { DIRECTION, routes } from 'constants/config';
import request from 'utils/request_ajax';
import Skeleton from 'ui/Skeleton';
import {
  HEADING_VARIANTS,
  Heading,
  Text,
  TEXT_VARIANTS,
} from 'components/ui/Typography';
import Button from 'components/ui/Button';
import Error from 'components/InPersonTraining/shared/Error';
import QuestionElementPrompt from 'components/Questions/shared/QuestionElementPrompt';
import SortingChoice from './SortingChoice';

function calculatePercent(choices) {
  return choices.filter(choice => choice.correct).length / choices.length;
}

const ASSISTIVE_TEXT_STATES = {
  GRABBING: 0,
  MOVING: 1,
  DROPPING: 2,
};

const SortingQuestion = ({ questionId, match, location }) => {
  const [disabledCheckAnswer, setDisabledCheckAnswer] = useState(true);
  const [disabledTryAgain, setDisabledTryAgain] = useState(false);
  const [choices, setChoices] = useState([]);
  const [a11yActive, setA11yActive] = useState(false);
  const [assistiveStatus, setAssistiveStatus] = useState(null);

  const isShowingResults = useMemo(() => {
    if (choices.length > 0) {
      return choices[0].correct !== undefined;
    }
    return false;
  }, [choices]);

  const correctAnswers = useMemo(() => {
    const correctPercent = calculatePercent(choices);
    if (correctPercent === 1) {
      setDisabledTryAgain(true);
    }
    return correctPercent;
  }, [choices]);

  const {
    data: sortingResult,
    loading,
    error,
  } = useFetch(`${match.url}/question/${questionId}${location.search}`);
  const isPreview = sortingResult?.meta?.previewing;
  const choiceClassNames = classNames('mb-12', {
    'disabled pe-none': isPreview,
  });

  function recordEvent(recordChoices) {
    const eventTrackingData = {
      browser_information: true,
      event_type: 'knowledge_check/attempt',
      event_data: {
        question_id: questionId,
        attempt_data: {
          correct: calculatePercent(recordChoices) === 1,
          choice_id: null,
          choice_text: recordChoices.map(c => c.text).toString(),
        },
      },
    };
    request.postJSON(routes.EVENTS, eventTrackingData);
  }

  function htmlDecode(input) {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent;
  }

  function checkAnswer() {
    request
      .postJSON(sortingResult?.meta?.saveUrl, {
        answer: choices.map(choice => choice.text),
        question_id: questionId,
      })
      .then(response => {
        let newChoices = [];
        setChoices(prevChoices => {
          newChoices = prevChoices.map((choice, index) => ({
            ...choice,
            correct:
              htmlDecode(choice.text) ===
              htmlDecode(response.answer.answer.correct_answer[index]),
          }));

          return newChoices;
        });
        recordEvent(newChoices);
      })
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert(t('questions.question.knowledge_check.error'));
        window.location.reload();
      });
  }

  function tryAgain() {
    setChoices(prevChoices =>
      prevChoices.map(choice => {
        delete choice.correct;
        return choice;
      }),
    );
  }

  useEffect(() => {
    if (!loading && !error) {
      const orderedCorrectChoices = orderBy(
        sortingResult?.data?.choices,
        'position',
      );
      if (sortingResult.meta.finished) {
        // progress finished so show given answer as correct
        setChoices(
          sortingResult?.data?.givenAnswer.map((text, id) => ({
            id,
            text,
            correct: true,
          })),
        );
      } else if (
        sortingResult?.data?.givenAnswer &&
        isEqual(
          orderedCorrectChoices.map(choice => choice.text),
          sortingResult?.data?.correctAnswer,
        ) &&
        xor(
          sortingResult?.data?.givenAnswer,
          orderedCorrectChoices.map(choice => choice.text),
        ).length === 0
      ) {
        // Question was previously answered and question has not changed
        const givenAnswerChoices = [];
        sortingResult?.data?.givenAnswer.forEach(answer => {
          const matchingChoice = sortingResult?.data?.choices.find(
            choice => choice.text === answer,
          );
          givenAnswerChoices.push({
            ...matchingChoice,
            // Was the given answer last time correct
            correct:
              orderedCorrectChoices[givenAnswerChoices.length].text === answer,
          });
        });
        setChoices(givenAnswerChoices);
      } else {
        setChoices(sortingResult?.data?.choices);
      }
    }
    return () => {
      setChoices([]);
    };
  }, [error, loading, sortingResult]);

  const swapArrayItems = (arr, index1, index2) => {
    const copy = [...arr];
    [copy[index1], copy[index2]] = [copy[index2], copy[index1]];
    return copy;
  };

  function reorder(direction, index) {
    setDisabledCheckAnswer(false);
    const reorderedChoices = swapArrayItems(
      choices,
      index,
      direction === DIRECTION.UP ? index - 1 : index + 1,
    );
    return reorderedChoices;
  }

  const moveChoice = useCallback((dragIndex, hoverIndex) => {
    setDisabledCheckAnswer(false);
    setChoices(prevChoices =>
      update(prevChoices, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevChoices[dragIndex]],
        ],
      }),
    );
  }, []);

  const [animatingIndexes, setAnimatingIndexes] = useState(null);
  const [timer, setTimer] = useState(null);
  const handleSwapAnimation = useCallback(
    change => {
      setAnimatingIndexes(change);
      if (timer) {
        clearTimeout(timer);
        setTimer(null);
      }
      setTimer(
        setTimeout(() => {
          setAnimatingIndexes(null);
        }, 300),
      );
      return () => {
        clearTimeout(timer);
      };
    },
    [timer],
  );

  const SortingChoiceLoader = () => (
    <div>
      <div className="mb-12">
        <Skeleton height={52} width="100%" />
      </div>
      <div className="mb-12">
        <Skeleton height={52} width="100%" />
      </div>
      <div className="mb-12">
        <Skeleton height={52} width="100%" />
      </div>
      <div className="mb-12">
        <Skeleton height={52} width="100%" />
      </div>
    </div>
  );

  if (error) {
    return <Error />;
  }

  if (loading) {
    return (
      <div className="SortingQuestion-loading">
        <div className="mb-24">
          <Heading component="h5" variant={HEADING_VARIANTS.H5}>
            <Skeleton width="75%" />
          </Heading>
        </div>
        <SortingChoiceLoader />
      </div>
    );
  }

  const AssistiveText = () => {
    let grabbedText = '';

    switch (assistiveStatus?.state) {
      case ASSISTIVE_TEXT_STATES.GRABBING:
        grabbedText = t(
          'learning.lessons.assistive_text.drag_and_drop.item_grabbed',
          {
            item: assistiveStatus?.item,
            position: assistiveStatus?.position,
            total: choices.length,
          },
        );
        break;
      case ASSISTIVE_TEXT_STATES.MOVING:
        grabbedText = t(
          'learning.lessons.assistive_text.drag_and_drop.item_moved',
          {
            item: assistiveStatus?.item,
            position: assistiveStatus?.position,
            total: choices.length,
          },
        );
        break;
      case ASSISTIVE_TEXT_STATES.DROPPING:
        grabbedText = t(
          'learning.lessons.assistive_text.drag_and_drop.item_dropped',
          {
            item: assistiveStatus?.item,
            position: assistiveStatus?.position,
            total: choices.length,
          },
        );
        break;
      default:
        break;
    }
    return (
      <>
        <span
          id={`sortingDescription-${questionId}`}
          className="SortingQuestion-assistive"
        >
          {assistiveStatus?.state === ASSISTIVE_TEXT_STATES.MOVING ||
          assistiveStatus?.state === ASSISTIVE_TEXT_STATES.GRABBING
            ? t('learning.lessons.assistive_text.drag_and_drop.press_to_drop')
            : t(
                'learning.lessons.assistive_text.drag_and_drop.press_to_reorder',
              )}
        </span>
        <span
          role="region"
          id={`sortingInfo-${questionId}`}
          aria-live="assertive"
          className="SortingQuestion-assistive"
        >
          <span>{grabbedText}</span>
        </span>
      </>
    );
  };

  if (choices.length > 0) {
    return (
      <>
        <div className="KnowledgeCheckShow">
          <div className="Label Label--brand CustomColor--backgroundColor ">
            {t('questions.question.knowledge_check.badge')}
          </div>
        </div>
        <QuestionElementPrompt
          elementHeaderClass={sortingResult.meta.elementHeaderClass}
          isGradable={sortingResult.data.isGradable}
          isPractice={sortingResult.data.isPractice}
          labelErrorClass={sortingResult.meta.labelErrorClass}
          questionSubjectClass={sortingResult.meta.questionSubjectClass}
          required={sortingResult.data.required}
          subject={sortingResult.data.subject}
        />
        {isShowingResults && (
          <div className="mt-12 d-f fd-r ai-c">
            {correctAnswers === 1 ? (
              <>
                <Text
                  className="d-f ai-c"
                  variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD_SUCCESS}
                >
                  <Check className="mr-4" />
                  {choices.filter(choice => choice.correct).length}/
                  {choices.length} {t('grades.correct')}
                </Text>
              </>
            ) : (
              <>
                <Text
                  className="d-f ai-c"
                  variant={TEXT_VARIANTS.INSTRUX_ERROR}
                >
                  <X className="mr-4" />
                  {choices.filter(choice => choice.correct).length}/
                  {choices.length} {t('grades.correct')}
                </Text>
              </>
            )}
          </div>
        )}
        <AssistiveText />
        <div
          className="mt-20"
          aria-controls={`sortingInfo-${questionId}`}
          aria-describedby={`sortingDescription-${questionId}`}
        >
          {choices.map((choice, index) => (
            <div key={choice.id} className={choiceClassNames}>
              <SortingChoice
                index={index}
                disabledOption={{
                  up: isPreview || index === 0,
                  down:
                    isPreview ||
                    index === sortingResult?.data?.choices.length - 1,
                }}
                onReorder={direction => {
                  if (direction === DIRECTION.UP) {
                    handleSwapAnimation({
                      movingUpIndex: index,
                      movingDownIndex: index - 1,
                    });
                    setAssistiveStatus({
                      state: ASSISTIVE_TEXT_STATES.MOVING,
                      position: index,
                      item: choice?.text,
                    });
                  } else {
                    handleSwapAnimation({
                      movingUpIndex: index + 1,
                      movingDownIndex: index,
                    });
                    setAssistiveStatus({
                      state: ASSISTIVE_TEXT_STATES.MOVING,
                      position: index + 2,
                      item: choice?.text,
                    });
                  }
                  const reorderedChoices = reorder(direction, index);
                  setChoices(reorderedChoices);
                }}
                moveChoice={moveChoice}
                className={classNames({
                  // Determine animation classes based on the index AFTER they were reordered
                  'SortingQuestion-Animation--translateUp':
                    animatingIndexes?.movingUpIndex === index + 1,
                  'SortingQuestion-Animation--translateDown':
                    animatingIndexes?.movingDownIndex === index - 1,
                })}
                a11yActive={a11yActive}
                handleA11y={() => {
                  if (isPreview) return;
                  setAssistiveStatus({
                    state: !a11yActive
                      ? ASSISTIVE_TEXT_STATES.GRABBING
                      : ASSISTIVE_TEXT_STATES.DROPPING,
                    position: index + 1,
                    item: choice?.text,
                  });
                  setA11yActive(!a11yActive);
                }}
                questionId={questionId}
                {...choice}
              />
            </div>
          ))}
        </div>
        <div className="d-f fd-rr mt-16">
          {isShowingResults ? (
            <Button
              type="button"
              ariaLabel={t('questions.question.knowledge_check.try_again')}
              disabled={disabledTryAgain}
              onClick={() => tryAgain()}
              icon="refresh"
            >
              {t('questions.question.knowledge_check.try_again')}
            </Button>
          ) : (
            <Button
              type="button"
              ariaLabel={t('questions.question.knowledge_check.check_answer')}
              disabled={disabledCheckAnswer}
              onClick={() => checkAnswer()}
            >
              {t('questions.question.knowledge_check.check_answer')}
            </Button>
          )}
        </div>
      </>
    );
  }

  return <Error />;
};

const SortingQuestionRoot = ({ questionId }) => {
  return (
    <BrowserRouter>
      <Switch>
        <Route
          path={[
            '/lesson/:lessonId',
            '/library/lesson/:lessonId',
            '/preview/lesson/:lessonId',
            '/path/:pathId/lesson/:lessonId',
            '/library/path/:pathId/lesson/:lessonId',
            // Not implemented
            '/preview/path/:pathId/lesson/:lessonId',
          ]}
          exact
          render={props => (
            <SortingQuestion questionId={questionId} {...props} />
          )}
        />
      </Switch>
    </BrowserRouter>
  );
};
export default props => <SortingQuestionRoot {...props} />;
